import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { AdminUser } from '@/api/types'
import { Pages, SortHead } from '@/features/admin/components/listing'
import { useAdminUsers } from '@/features/admin/queries'
import { SearchBox } from '@/shared/components/search-box'
import { UserAvatar } from '@/shared/components/user-avatar'
import { Badge } from '@/shared/ui/badge'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table'

const ROUTE = '/shell/admin/users'
const WHEN = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

/**
 * Every account on the instance, to read.
 *
 * **Nothing here changes an account**, because nothing about one is this
 * application's: the realm creates accounts, decides who may sign in, decides
 * who holds staff and removes them, and `core.idp_feed` projects all of it. A
 * name, a suspension or a deletion applied here would be reverted by the next
 * snapshot pass, so the table offers none of them. The backend agrees -- there
 * is no per-account endpoint at all.
 *
 * Impersonation is not here either, and that is a real absence rather than an
 * oversight -- upstream's runs as server-side middleware on first page load
 * (`if (!import.meta.server) return`), so it stops working the moment there is
 * no server rendering the first page. Bringing it back means teaching the
 * session to hold a second identity without disturbing the administrator's own,
 * which is its own piece of work.
 */
export function AdminUsersPage() {
  const { t } = useTranslation()
  const listing = useSearch({ from: ROUTE })
  const navigate = useNavigate()
  const users = useAdminUsers(listing)

  function go(changes: Partial<typeof listing>): void {
    void navigate({
      to: '/admin/users',
      // Any change to what is being asked for starts at the first page again:
      // page four of one search is not page four of another.
      search: { ...listing, page: 1, ...changes },
      replace: true,
    })
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold">{t('admin.usersTitle')}</h1>
        <SearchBox
          term={listing.search}
          label={t('admin.searchUsers')}
          className="h-8 w-56 pr-7 pl-7 bg-background on-surface"
          onSearch={(term) => {
            go({ search: term })
          }}
        />
      </header>

      {users.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead
                  field="username"
                  label={t('admin.address')}
                  listing={listing}
                  onSort={(sort) => {
                    go({ sort })
                  }}
                />
                <SortHead
                  field="name"
                  label={t('admin.name')}
                  listing={listing}
                  onSort={(sort) => {
                    go({ sort })
                  }}
                />
                <TableHead>{t('admin.workspaces')}</TableHead>
                <SortHead
                  field="last_login"
                  label={t('admin.lastSignIn')}
                  listing={listing}
                  onSort={(sort) => {
                    go({ sort })
                  }}
                />
                <SortHead
                  field="date_joined"
                  label={t('admin.joined')}
                  listing={listing}
                  onSort={(sort) => {
                    go({ sort })
                  }}
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(users.data?.results ?? []).map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </TableBody>
          </Table>

          {users.data?.results.length === 0 && (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t('admin.noUsers')}
            </p>
          )}

          <Pages
            count={users.data?.count ?? 0}
            listing={listing}
            onPage={(page) => {
              void navigate({
                to: '/admin/users',
                search: { ...listing, page },
                replace: true,
              })
            }}
          />
        </>
      )}
    </div>
  )
}

function UserRow({ user }: { user: AdminUser }) {
  const { t } = useTranslation()

  return (
    <TableRow>
      <TableCell className="font-medium">
        <span className="flex items-center gap-2">
          <UserAvatar src={user.avatar_url} seed={user.username} className="size-6" />
          <span className="truncate">{user.username}</span>
          {user.is_staff === true && (
            <Badge variant="secondary">{t('admin.staff')}</Badge>
          )}
          {user.is_active === false && (
            <Badge variant="outline">{t('admin.deactivated')}</Badge>
          )}
        </span>
      </TableCell>
      <TableCell className="truncate">{user.name}</TableCell>
      <TableCell className="text-muted-foreground truncate text-sm">
        {user.workspaces.map((workspace) => workspace.name).join(', ')}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
        {/* Never signed in is not the same as signed in long ago, and a dash is
            the only honest thing to write for it. */}
        {user.last_login === null || user.last_login === undefined
          ? '—'
          : WHEN.format(new Date(user.last_login))}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
        {user.date_joined === undefined ? '—' : WHEN.format(new Date(user.date_joined))}
      </TableCell>
    </TableRow>
  )
}
